/**
 * Admin Trade Execution API
 * 
 * API functions to execute trade entry/exit scripts from the admin panel.
 */

import apiClient from "./apiClient";
import { logger } from "@app/services/logger";

export interface ScriptExecutionResult {
  success: boolean;
  message: string;
  symbol?: string;
  recommendation_id?: string;
  signal_id?: string;
  position_id?: string;
}

export interface PositionSearchItem {
  position_id: string;
  symbol: string;
  state: string;
  entry_price: number | null;
  exit_price: number | null;
  exit_reason: string | null;
  closed_at: string | null;
  created_at: string | null;
}

export interface PositionSearchResponse {
  positions: PositionSearchItem[];
  total: number;
}

export interface DeleteTradeResult {
  success: boolean;
  message: string;
  deleted_records: Record<string, number>;
}

/**
 * Execute entry script - enters first recommendation from queue
 */
export async function executeEntryFromQueue(
  symbol?: string,
): Promise<ScriptExecutionResult> {
  try {
    const response = await apiClient.post<ScriptExecutionResult>(
      "/v1/admin/trade-execution/entry",
      { symbol: symbol || null },
    );
    return response.data;
  } catch (error) {
    logger.error("Error executing entry script:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

/**
 * Execute exit script - exits from active position
 */
export async function executeExitFromPosition(
  symbol?: string,
): Promise<ScriptExecutionResult> {
  try {
    const response = await apiClient.post<ScriptExecutionResult>(
      "/v1/admin/trade-execution/exit",
      { symbol: symbol || null },
    );
    return response.data;
  } catch (error) {
    logger.error("Error executing exit script:", error as Error);
    throw error;
  }
}

/**
 * Search positions by symbol for trade deletion.
 */
export async function searchPositions(
  symbol: string,
  limit: number = 20,
): Promise<PositionSearchResponse> {
  try {
    const response = await apiClient.get<PositionSearchResponse>(
      `/v1/admin/trade-execution/search-positions`,
      { params: { symbol, limit } },
    );
    return response.data;
  } catch (error) {
    logger.error("Error searching positions:", error as Error);
    throw error;
  }
}

/**
 * Delete a trade and all linked records by position_id.
 */
export async function deleteTrade(
  positionId: string,
): Promise<DeleteTradeResult> {
  try {
    const response = await apiClient.delete<DeleteTradeResult>(
      `/v1/admin/trade-execution/delete-trade`,
      { data: { position_id: positionId } },
    );
    return response.data;
  } catch (error) {
    logger.error("Error deleting trade:", error as Error);
    throw error;
  }
}
